import React from 'react'
import {
  Box,
  Button,
  Flex,
  Heading,
  Icon,
  Text,
  VStack,
} from '@chakra-ui/react'
import { FiAlertTriangle, FiRefreshCw, FiHome } from 'react-icons/fi'
import { PRIMARY_COLOR } from '../config/theme'

/**
 * ErrorBoundary - Catches JavaScript errors anywhere in the child component tree
 * Usage: Wrap components or routes with <ErrorBoundary>
 */
class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props)
    this.state = { hasError: false, error: null, errorInfo: null }
  }

  static getDerivedStateFromError(error) {
    // Update state so the next render will show the fallback UI
    return { hasError: true, error }
  }

  componentDidCatch(error, errorInfo) {
    // Log error to console (could also send to error reporting service)
    console.error('ErrorBoundary caught an error:', error, errorInfo)
    this.setState({ errorInfo })
  }

  handleReset = () => {
    this.setState({ hasError: false, error: null, errorInfo: null })
  }

  render() {
    if (this.state.hasError) {
      // Custom fallback UI or use provided fallback
      if (this.props.fallback) {
        return this.props.fallback
      }

      return (
        <Flex
          minH="50vh"
          align="center"
          justify="center"
          p={8}
        >
          <VStack spacing={6} textAlign="center" maxW="md">
            <Icon as={FiAlertTriangle} boxSize={16} color="orange.400" />
            <Heading size="lg" color={PRIMARY_COLOR}>
              Something went wrong
            </Heading>
            <Text color="gray.600">
              {this.props.message || "We're sorry, but something unexpected happened. Please try refreshing the page."}
            </Text>
            
            {/* Show error details in development */}
            {process.env.NODE_ENV === 'development' && this.state.error && (
              <Box
                bg="red.50"
                p={4}
                borderRadius="md"
                w="full"
                textAlign="left"
                fontSize="sm"
                fontFamily="mono"
                color="red.700"
                maxH="200px"
                overflowY="auto"
              >
                <Text fontWeight="bold" mb={2}>Error Details:</Text>
                <Text>{this.state.error.toString()}</Text>
                {this.state.errorInfo && (
                  <Text mt={2} fontSize="xs" whiteSpace="pre-wrap">
                    {this.state.errorInfo.componentStack}
                  </Text>
                )}
              </Box>
            )}

            <Flex gap={4}>
              <Button
                leftIcon={<FiRefreshCw />}
                colorScheme="blue"
                onClick={() => {
                  this.handleReset()
                  window.location.reload()
                }}
              >
                Refresh Page
              </Button>
              <Button
                leftIcon={<FiHome />}
                variant="outline"
                as="a"
                href="/home"
              >
                Go Home
              </Button>
            </Flex>
          </VStack>
        </Flex>
      )
    }

    return this.props.children
  }
}

export default ErrorBoundary
